import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    useGetAdminUsersQuery,
    useSetAdminRolesMutation,
    useSetAdminStatusMutation,
    useLazyExpiredIdenfyKYCQuery,
    usePostPaxumDataMutation,
    useLazyRunTest1Query,
    useImpersonateMutation,
    useListTrainingPendingReviewsQuery,
    useApproveTrainingReviewMutation,
} from '../app/api';
import { impersonationStarted, selectAdminUuid, selectImpersonatorUuid } from '../features/auth/authSlice';
import { getRoleJSON } from './utils';
import {
  Button,
  Alert,
  Badge,
  Form,
  Card,
  Container,
  InputGroup
} from 'react-bootstrap';

// Roles that can be assigned to an admin. Order here drives display order in
// the checkbox group and the role-filter dropdown. EMPLOYEE is intentionally
// absent — every ACTIVE admin gets employee-level access implicitly, so
// storing it explicitly would just be noise.
// BETA and DEMO are last on purpose: neither is a department. BETA is the
// early-access group; DEMO swaps Your Day, Meetings, Tasks and My Team over to
// canned data so they can be shown to a room, and is meant to be taken off again
// once the demo is over — see api/app/demo_data.py.
// WORK_ADMIN sits next to WORK because it is the elevated tier OF Work, not a
// department of its own, and it does not imply WORK — a CSA needs both.
// CONFIDENTIAL_REVIEWER reads the Role Alignment answers — the one role that
// can, since TECH only runs the process.
// SUPER_ADMIN sits next to ADMIN: it is the only role that can see or assign
// the PROTECTED_ROLES below, and it does not imply ADMIN.
const ASSIGNABLE_ROLES = ['ADMIN', 'SUPER_ADMIN', 'TECH', 'HR', 'COMPLIANCE', 'EXEC', 'FINANCE', 'FINTECH', 'WORK', 'WORK_ADMIN', 'ENTREPRENEUR', 'MARKETING', 'CONFIDENTIAL_REVIEWER', 'BETA', 'DEMO'];
// Shown only to a SUPER_ADMIN. The server enforces it (PROTECTED_ROLES in
// api/app/db/users.py): for anyone else these never appear in the user list,
// can't be granted, and are kept as-is when that person's other roles are saved.
const PROTECTED_ROLES = ['SUPER_ADMIN', 'CONFIDENTIAL_REVIEWER'];
const STATUSES = ['ACTIVE', 'PENDING', 'DEACTIVATED'];

/* Roles that open at least one group behind the sidebar's Admin button.
 *
 * NOT simply "has any role", and the difference matters when you are writing to
 * people about a navigation change. Someone holding only WORK sees the Work
 * pill, not the Admin button; someone holding only BETA or DEMO sees neither.
 * Mailing them about a control they do not have is how a change announcement
 * loses its audience.
 *
 * NAMED for the internal identifier, not the label: `admin` is already the name
 * of a ROLE in this file (see ASSIGNABLE_ROLES), and ADMIN is only one of the
 * ten roles below. Same reasoning as `backstage` in routes/Root.jsx — keep this
 * in step with the `backstageMenu` groups there.
 */
const BACKSTAGE_ROLES = ['ADMIN', 'TECH', 'HR', 'COMPLIANCE', 'EXEC', 'FINANCE', 'FINTECH', 'ENTREPRENEUR', 'MARKETING', 'CONFIDENTIAL_REVIEWER'];

/* `roles: null` means no role filter at all — every active employee.
 *
 * The three audiences at the top are the ones a release announcement actually
 * splits into, and they are not the same people:
 *
 *   everyone   a feature going GA is NEW to them; there is nothing to un-learn
 *   beta       the same feature MOVED; they are the only ones for whom a
 *              reshuffle is a change rather than an arrival
 *   admin      a navigation change they will hit, whatever else is going on
 */
const AUDIENCES = [
  {
    key: 'backstage',
    label: 'Sees Admin button',
    hint: 'Anyone whose roles open a department console — the audience for an Admin-navigation announcement.',
    roles: BACKSTAGE_ROLES,
  },
  {
    key: 'everyone',
    label: 'Everyone (all active)',
    hint: 'Every active employee account, roles or not — the audience for "this is now available to all".',
    roles: null,
  },
  {
    key: 'beta',
    label: 'Beta group',
    hint: 'The pilot cohort. They are the only people for whom a graduated feature MOVED rather than appeared, '
        + 'so they are the ones a "where did Tasks go?" note is for.',
    roles: ['BETA'],
  },
  {
    key: 'any',
    label: 'Any role at all',
    hint: 'Everyone with something beyond the default employee access, including WORK, BETA and DEMO.',
    roles: ASSIGNABLE_ROLES,
  },
  {
    key: 'work',
    label: 'Work side',
    hint: 'The wealth-management workspace.',
    roles: ['WORK'],
  },
];

/* Addresses of the people with at least one of `roles`.
 *
 * ACTIVE only, always. A DEACTIVATED account is someone who has left, and a
 * PENDING one has never logged in; both would bounce or, worse, reach a personal
 * forwarder. Filtering here rather than offering it as a toggle because there is
 * no version of "email the team about a UI change" that wants either.
 */
function audienceFor(admins, roles) {
  // null = no role filter. Distinct from an empty list, which would match
  // nobody — worth keeping apart, since "everyone" and "nobody" are a typo
  // away from each other here.
  const wanted = roles === null ? null : new Set(roles);
  return admins
    .filter((a) => a.status === 'ACTIVE')
    .filter((a) => wanted === null || (a.roles || []).some((r) => wanted.has(r)))
    .map((a) => ({
      email: a.username,
      name: [a.first_name, a.last_name].filter(Boolean).join(' '),
      roles: a.roles || [],
    }))
    .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
}

/* Who to email about a change, built from the table already on this page.
 *
 * No new endpoint: the role data is right here, so a second source would be one
 * more thing to drift. Addresses are joined with '; ' because that is what
 * Outlook's To/Bcc field expects — a comma-separated list pastes as a single
 * malformed recipient in some Outlook builds.
 */
function MailingList({ admins }) {
  const [audienceKey, setAudienceKey] = useState('backstage');
  const [copied, setCopied] = useState(false);
  const [showNames, setShowNames] = useState(false);

  const audience = AUDIENCES.find((a) => a.key === audienceKey) || AUDIENCES[0];
  const people = useMemo(() => audienceFor(admins, audience.roles), [admins, audience]);
  const addresses = people.map((p) => p.email).join('; ');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(addresses);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is blocked on http:// origins and in some locked-down
      // browsers. Falling back to the textarea (already on screen) is better
      // than an error nobody can act on.
      setShowNames(false);
    }
  };

  const downloadCsv = () => {
    const rows = [['email', 'name', 'roles']].concat(
      people.map((p) => [p.email, p.name, p.roles.join(' ')]),
    );
    const csv = rows
      .map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `hub-${audience.key}-recipients.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Next tick — revoking synchronously can cancel the download in Safari.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return (
    <div className="card mb-4">
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        <h5 className="mb-0">
          <i className="bi bi-envelope-paper me-2" />
          Mailing list ({people.length})
        </h5>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <Form.Select size="sm" style={{ width: 200 }} value={audienceKey}
            onChange={(e) => setAudienceKey(e.target.value)} aria-label="Audience">
            {AUDIENCES.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
          </Form.Select>
          <Button size="sm" variant={copied ? 'success' : 'primary'} onClick={copy}
            disabled={people.length === 0}>
            <i className={`bi bi-${copied ? 'check2' : 'clipboard'} me-1`} />
            {copied ? 'Copied' : 'Copy addresses'}
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={downloadCsv}
            disabled={people.length === 0}>
            <i className="bi bi-download me-1" /> CSV
          </Button>
          <Button size="sm" variant="link" className="px-1"
            onClick={() => setShowNames((v) => !v)}>
            {showNames ? 'Hide who' : 'Show who'}
          </Button>
        </div>
      </div>
      <div className="card-body">
        <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
          {audience.hint} Active accounts only — deactivated and pending are left out.
        </p>
        <Form.Control
          as="textarea"
          rows={3}
          readOnly
          value={addresses}
          onFocus={(e) => e.target.select()}
          style={{ fontSize: '0.78rem', fontFamily: 'monospace' }}
          aria-label="Recipient addresses"
        />
        {showNames && (
          <div className="mt-3" style={{ maxHeight: 260, overflowY: 'auto' }}>
            <table className="table table-sm mb-0">
              <tbody>
                {people.map((p) => (
                  <tr key={p.email}>
                    <td style={{ width: '30%' }}>{p.name || <span className="text-muted">—</span>}</td>
                    <td style={{ width: '40%' }}>{p.email}</td>
                    <td>
                      {p.roles.map((r) => (
                        <Badge key={r} bg="light" text="dark" className="me-1">{r}</Badge>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
MailingList.propTypes = { admins: PropTypes.array };
MailingList.defaultProps = { admins: [] };


// Inline queue of training courses that Compliance has submitted for admin
// approval. Approve from here goes straight through; Reject opens the course
// in the editor where the admin can leave a reason.
function PendingTrainingApprovals() {
  const navigate = useNavigate();
  const { data: pending = [], isLoading } = useListTrainingPendingReviewsQuery();
  const [approve, { isLoading: isApproving }] = useApproveTrainingReviewMutation();
  const [busyId, setBusyId] = useState(null);

  if (isLoading || pending.length === 0) return null;

  const handleApprove = async (courseId) => {
    setBusyId(courseId);
    try {
      await approve({ id: courseId }).unwrap();
    } finally {
      setBusyId(null);
    }
  };

  const requesterLabel = (row) => {
    const name = `${row.requested_by_first_name || ''} ${row.requested_by_last_name || ''}`.trim();
    return name || row.requested_by_username || row.requested_by || 'Unknown';
  };

  return (
    <div className="card mb-4" style={{ borderColor: '#b06f00' }}>
      <div className="card-header d-flex align-items-center gap-2" style={{ background: 'rgba(176,111,0,0.10)' }}>
        <i className="bi bi-hourglass-split" style={{ color: '#b06f00', fontSize: '1.1rem' }} />
        <h5 className="mb-0">Training courses awaiting approval ({pending.length})</h5>
      </div>
      <ul className="list-group list-group-flush">
        {pending.map(row => (
          <li key={row.id} className="list-group-item d-flex align-items-center gap-3 flex-wrap">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="fw-bold">{row.title}</div>
              <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                Submitted by {requesterLabel(row)}
                {row.requested_at ? ` · ${new Date(row.requested_at).toLocaleString()}` : ''}
                {' · '}
                {row.module_count || 0} module{row.module_count === 1 ? '' : 's'}
              </div>
            </div>
            <div className="d-flex gap-2 flex-shrink-0">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => window.open(`/training/${row.id}`, '_blank', 'noopener')}
                title="Open the course as a learner would see it"
              >
                <i className="bi bi-eye me-1" />
                Preview
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={() => navigate(`/compliance/training/${row.id}`)}>
                Review
              </Button>
              <Button
                size="sm"
                variant="success"
                onClick={() => handleApprove(row.id)}
                disabled={busyId === row.id || isApproving}
              >
                <i className="bi bi-check2-circle me-1" />
                {busyId === row.id ? 'Approving…' : 'Approve'}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Admin() {

  const { data: adminData, isLoading: isDataLoading, error, refetch } = useGetAdminUsersQuery();
  const [setAdminRoles] = useSetAdminRolesMutation();
  const [setAdminStatus] = useSetAdminStatusMutation();
  const [getExpiredKYC] = useLazyExpiredIdenfyKYCQuery();
  const [postPaxumData] = usePostPaxumDataMutation();
  const [dbtests] = useLazyRunTest1Query();
  const [impersonate] = useImpersonateMutation();
  const dispatch = useDispatch();
  const currentAdminUuid = useSelector(selectAdminUuid);
  const alreadyImpersonating = Boolean(useSelector(selectImpersonatorUuid));
  const [impersonating, setImpersonating] = useState({});
  // Per-admin "saving role changes" indicator, keyed by uuid.
  const [savingRoles, setSavingRoles] = useState({});
  const [savingStatus, setSavingStatus] = useState({});
  // Draft role selections, keyed by uuid. Kept separate from the server-side
  // roles so the user can toggle multiple boxes before hitting Save without
  // each toggle producing a network round-trip.
  const [draftRoles, setDraftRoles] = useState({});
  const [message, setMessage] = useState(null);
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [loginWindow, setLoginWindow] = useState('ALL');
  const [advGoToPage, setAdvGoToPage] = useState('/expired_docs');

  const LOGIN_WINDOW_DAYS = { '2D': 2, '1W': 7, '1M': 30 };

  // Only a SUPER_ADMIN is offered the protected roles. Starts false, so the
  // checkboxes appear once the lookup confirms it rather than flashing up for
  // everyone first.
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (currentAdminUuid) {
      getRoleJSON(currentAdminUuid).then(rj => { if (!cancelled) setIsSuperAdmin(Boolean(rj?.isSuperAdmin)); });
    }
    return () => { cancelled = true; };
  }, [currentAdminUuid]);
  const assignableRoles = isSuperAdmin
    ? ASSIGNABLE_ROLES
    : ASSIGNABLE_ROLES.filter(r => !PROTECTED_ROLES.includes(r));

  const admins = adminData?.admins || [];

  // Seed draft selections from the server-supplied roles whenever a new
  // admin list arrives. Without this the checkboxes stay empty on first
  // load because draftRoles starts as {}.
  useEffect(() => {
    if (!admins.length) return;
    setDraftRoles(prev => {
      const next = { ...prev };
      admins.forEach(a => {
        if (!(a.uuid in next)) next[a.uuid] = a.roles || [];
      });
      return next;
    });
  }, [admins]);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const loginCutoffMs = LOGIN_WINDOW_DAYS[loginWindow]
    ? Date.now() - LOGIN_WINDOW_DAYS[loginWindow] * 24 * 60 * 60 * 1000
    : null;
  const filteredAdmins = admins.filter(admin => {
    if (roleFilter !== 'ALL' && !(admin.roles || []).includes(roleFilter)) return false;
    if (statusFilter !== 'ALL' && admin.status !== statusFilter) return false;
    if (normalizedQuery) {
      // Match username, either name, and the full name — so "jen schell" hits
      // even though first and last are separate fields. Names come from
      // company_directory; service accounts have none and match on username only.
      const full = `${admin.first_name || ''} ${admin.last_name || ''}`.trim();
      const haystack = [admin.username, admin.first_name, admin.last_name, full]
        .filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(normalizedQuery)) return false;
    }
    if (loginCutoffMs !== null) {
      if (!admin.last_login) return false;
      if (new Date(admin.last_login).getTime() < loginCutoffMs) return false;
    }
    return true;
  });

  const handleAdvancedDevClick = async (type) => {
    if(type == 'expired kyc data'){
        const x = await getExpiredKYC();
        console.log(x.data);
    }
    if(type == 'run paxum api test'){
        const x = await postPaxumData({data: 'hello'});
        console.log(x.data);
    }
    if(type == 'run db tests'){
        const x = await dbtests({client_id: 'affiliate:1036812'});
        console.log(x.data);
    }
  }

  const handleImpersonate = async (admin) => {
    if (alreadyImpersonating) {
      setMessage({ type: 'warning', text: 'End the current impersonation session before starting a new one.' });
      return;
    }
    const confirmed = window.confirm(
      `Sign in as ${admin.username}? You will assume their session and be able to return to your admin session via the banner.`
    );
    if (!confirmed) return;

    setImpersonating(prev => ({ ...prev, [admin.uuid]: true }));
    try {
      const res = await impersonate({ target_admin_uuid: admin.uuid }).unwrap();
      dispatch(impersonationStarted({
        accessToken: res.access_token,
        targetUuid: res.uuid,
        targetEmail: res.username,
        actorUuid: res.impersonator_uuid,
        actorEmail: res.impersonator_username,
      }));
      window.location.assign('/dashboard');
    } catch (err) {
      setMessage({ type: 'danger', text: err?.data?.detail || 'Could not impersonate this user.' });
      setImpersonating(prev => ({ ...prev, [admin.uuid]: false }));
    }
  };

  // Toggle a role in the local draft for this admin. Save happens via the
  // explicit "Save" button so the user can stage several toggles cheaply.
  const toggleDraftRole = (uuid, role) => {
    setDraftRoles(prev => {
      const current = new Set(prev[uuid] || []);
      if (current.has(role)) current.delete(role);
      else current.add(role);
      return { ...prev, [uuid]: Array.from(current) };
    });
  };

  const handleSaveRoles = async (admin) => {
    const desired = (draftRoles[admin.uuid] || []).slice().sort();
    setSavingRoles(prev => ({ ...prev, [admin.uuid]: true }));
    try {
      await setAdminRoles({ uuid: admin.uuid, roles: desired }).unwrap();
      setMessage({ type: 'success', text: `Roles updated for ${admin.username}.` });
      refetch();
    } catch (err) {
      setMessage({ type: 'danger', text: err?.data?.detail || 'Failed to update roles.' });
    } finally {
      setSavingRoles(prev => ({ ...prev, [admin.uuid]: false }));
    }
  };

  const handleStatusChange = async (admin, newStatus) => {
    if (newStatus === admin.status) return;
    setSavingStatus(prev => ({ ...prev, [admin.uuid]: true }));
    try {
      await setAdminStatus({ uuid: admin.uuid, status: newStatus }).unwrap();
      setMessage({ type: 'success', text: `Status updated for ${admin.username}.` });
      refetch();
    } catch (err) {
      setMessage({ type: 'danger', text: err?.data?.detail || 'Failed to update status.' });
    } finally {
      setSavingStatus(prev => ({ ...prev, [admin.uuid]: false }));
    }
  };

  // True iff the user has changed the draft selection vs. what's saved on
  // the server, so the Save button can be disabled when nothing is pending.
  const isDirty = (admin) => {
    const draft = (draftRoles[admin.uuid] || []).slice().sort().join(',');
    const saved = (admin.roles || []).slice().sort().join(',');
    return draft !== saved;
  };

  const formatLastSignedIn = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const fullSignInTimestamp = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US');
  };

  if (isDataLoading) {
    return (
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid py-4">
        <Alert variant="danger">
          Error loading admin users. Please try again.
        </Alert>
      </div>
    );
  }

  return (
    // Tighter base font so the user/role table stays readable as the row
    // count grows — scoped to this page only via inline style.
    <div className="container-fluid py-4" style={{ fontSize: '0.85rem' }}>
      <PendingTrainingApprovals />

      <div className="mb-4">
        <h4 className="mb-1">Admin User Management</h4>
      </div>

      {message && (
        <Alert
          variant={message.type}
          dismissible
          onClose={() => setMessage(null)}
          className="mb-4"
        >
          {message.text}
        </Alert>
      )}

      <MailingList admins={admins} />

      <div className="card">
        <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
          <h5 className="mb-0">Users ({filteredAdmins.length}{filteredAdmins.length !== admins.length ? ` of ${admins.length}` : ''})</h5>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <InputGroup size="sm" style={{ width: '240px' }}>
              <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
              <Form.Control
                type="search"
                placeholder="Search name or username"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search admin users by username"
              />
            </InputGroup>
            <Form.Label className="mb-0">Has role:</Form.Label>
            <Form.Select
              size="sm"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              style={{ width: '140px' }}
            >
              <option value="ALL">Any role</option>
              {assignableRoles.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </Form.Select>
            <Form.Label className="mb-0">Status:</Form.Label>
            <Form.Select
              size="sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ width: '140px' }}
            >
              <option value="ALL">Any status</option>
              {STATUSES.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Form.Select>
            <Form.Label className="mb-0">Logged in:</Form.Label>
            <Form.Select
              size="sm"
              value={loginWindow}
              onChange={(e) => setLoginWindow(e.target.value)}
              style={{ width: '140px' }}
            >
              <option value="ALL">Any time</option>
              <option value="2D">Within 2 days</option>
              <option value="1W">Within 1 week</option>
              <option value="1M">Within 1 month</option>
            </Form.Select>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-sm table-hover mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>Username</th>
                <th>Status</th>
                <th>Additional Roles</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAdmins.map(admin => {
                const draft = draftRoles[admin.uuid] || [];
                const isSelf = admin.uuid === currentAdminUuid;
                return (
                <tr key={admin.uuid}>
                  <td>
                    {/* Name leads when we have one; the username stays visible
                        underneath since it is the identifier everything else
                        (impersonation, roles, support requests) refers to. */}
                    {(admin.first_name || admin.last_name) ? (
                      <>
                        <div className="fw-medium">
                          {`${admin.first_name || ''} ${admin.last_name || ''}`.trim()}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                          {admin.username}
                        </div>
                      </>
                    ) : (
                      <div className="fw-medium">{admin.username}</div>
                    )}
                  </td>
                  <td style={{ minWidth: '140px' }}>
                    <Form.Select
                      size="sm"
                      value={admin.status}
                      disabled={isSelf || savingStatus[admin.uuid]}
                      onChange={(e) => handleStatusChange(admin, e.target.value)}
                      title={isSelf ? 'You cannot change your own status' : ''}
                      style={{ width: '130px' }}
                    >
                      {STATUSES.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </Form.Select>
                  </td>
                  <td style={{ minWidth: '320px' }}>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      {assignableRoles.map(role => {
                        const checked = draft.includes(role);
                        const isProtected = PROTECTED_ROLES.includes(role);
                        // The server refuses this too; disabling it says so up front.
                        const ownSuperAdmin = isSelf && role === 'SUPER_ADMIN' && (admin.roles || []).includes(role);
                        return (
                          <Form.Check
                            key={role}
                            type="checkbox"
                            id={`role-${admin.uuid}-${role}`}
                            checked={checked}
                            disabled={savingRoles[admin.uuid] || ownSuperAdmin}
                            onChange={() => toggleDraftRole(admin.uuid, role)}
                            title={ownSuperAdmin
                              ? 'You cannot remove your own Super Admin role'
                              : isProtected ? 'Only Super Admins can see or change this role' : ''}
                            label={
                              <span
                                className={`badge me-1 ${checked
                                  ? (isProtected ? 'bg-dark' : 'bg-primary')
                                  : 'bg-light text-muted border'}`}
                              >
                                {isProtected && <i className="bi bi-lock-fill me-1" />}
                                {role}
                              </span>
                            }
                          />
                        );
                      })}
                    </div>
                  </td>
                  <td title={fullSignInTimestamp(admin.last_login)}>
                    {admin.last_login
                      ? formatLastSignedIn(admin.last_login)
                      : <span className="text-muted">Never</span>}
                  </td>
                  <td>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={!isDirty(admin) || savingRoles[admin.uuid]}
                        onClick={() => handleSaveRoles(admin)}
                      >
                        {savingRoles[admin.uuid] ? (
                          <span className="spinner-border spinner-border-sm me-1" />
                        ) : null}
                        Save roles
                      </Button>
                      {(() => {
                        const disabled =
                          isSelf
                          || admin.status !== 'ACTIVE'
                          || alreadyImpersonating
                          || impersonating[admin.uuid];
                        const title = isSelf
                          ? 'This is you'
                          : admin.status !== 'ACTIVE'
                          ? 'Only ACTIVE admins can be impersonated'
                          : alreadyImpersonating
                          ? 'End the current impersonation first'
                          : `Sign in as ${admin.username}`;
                        if (disabled) {
                          return (
                            <span className="text-muted" title={title} style={{ cursor: 'not-allowed' }}>
                              {impersonating[admin.uuid] && (
                                <span className="spinner-border spinner-border-sm me-1" />
                              )}
                              Sign in as
                            </span>
                          );
                        }
                        return (
                          <a
                            href="#"
                            title={title}
                            onClick={(e) => { e.preventDefault(); handleImpersonate(admin); }}
                          >
                            Sign in as
                          </a>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* development functionality / testing */}
      <Container className='my-4 hidden'>
        <Card>
            <Card.Header>Advanced Development Features</Card.Header>
            <Card.Body>
                <Card.Title className='my-3'>Run Test</Card.Title>
                <Button variant="primary" onClick={ ()=> handleAdvancedDevClick('run db tests') }>Run</Button>
            </Card.Body>
            <Card.Body>
                <Card.Title className='my-3'>Go To Page:</Card.Title>
                <input type="text" id="adminGoTo" value={advGoToPage} onChange={(e)=>setAdvGoToPage(e.target.value)}/>
                <Button variant="primary" onClick={ ()=> window.location = advGoToPage }>GO</Button>
            </Card.Body>
        </Card>
      </Container>


    </div>
  );
}
